const MODERN_PAGES_PANEL_HOSTS = new Set(['EDITOR3', 'STUDIO2']);

export type PageInfoPanelTheme = 'classic' | 'odeditor';

type ModernPagesPanelGlobal = {
  location?: { search?: string };
  commonConfig?: { host?: string };
  editorModel?: {
    siteHeader?: {
      builderComponentModel?: unknown;
    };
  };
};

function isModernPagesPanelGlobal(
  candidate: ModernPagesPanelGlobal | undefined,
): candidate is ModernPagesPanelGlobal {
  return Boolean(candidate);
}

const getHost = ({ location, commonConfig }: ModernPagesPanelGlobal) => {
  try {
    const encodedCommonConfig = new URLSearchParams(location?.search).get(
      'commonConfig',
    );
    const host = encodedCommonConfig
      ? JSON.parse(encodedCommonConfig)?.host
      : undefined;

    if (host) {
      return String(host).toUpperCase();
    }
  } catch {
    // Malformed commonConfig query parameter; use the runtime fallback.
  }

  return String(commonConfig?.host ?? '').toUpperCase();
};

export const getEditorHostFromGlobals = (
  globalCandidates: ModernPagesPanelGlobal[],
): string | undefined => {
  return globalCandidates.map(getHost).find(Boolean) || undefined;
};

export const isModernPagesPanelEditorFromGlobals = (
  globalCandidates: ModernPagesPanelGlobal[],
): boolean => {
  if (
    globalCandidates.some(
      ({ editorModel }) => editorModel?.siteHeader?.builderComponentModel,
    )
  ) {
    return true;
  }

  return globalCandidates.some((globalCandidate) =>
    MODERN_PAGES_PANEL_HOSTS.has(getHost(globalCandidate)),
  );
};

export const getPageInfoPanelThemeFromGlobals = (
  globalCandidates: ModernPagesPanelGlobal[],
): PageInfoPanelTheme => {
  return globalCandidates.some(
    (globalCandidate) => getHost(globalCandidate) === 'STUDIO2',
  )
    ? 'odeditor'
    : 'classic';
};

const getModernPagesPanelGlobals = () =>
  [
    typeof self === 'undefined'
      ? undefined
      : (self as ModernPagesPanelGlobal),
    typeof window === 'undefined'
      ? undefined
      : (window as unknown as ModernPagesPanelGlobal),
    globalThis as ModernPagesPanelGlobal,
  ].filter(isModernPagesPanelGlobal);

export const isModernPagesPanelEditor = (): boolean => {
  return isModernPagesPanelEditorFromGlobals(getModernPagesPanelGlobals());
};

export const getPageInfoPanelTheme = (): PageInfoPanelTheme => {
  return getPageInfoPanelThemeFromGlobals(getModernPagesPanelGlobals());
};

export const getEditorHost = (): string | undefined => {
  return getEditorHostFromGlobals(getModernPagesPanelGlobals());
};
